import { UserMinus, X } from 'lucide-react';
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/db';
import { captureSilentError } from '../../services/posthog';
import { User } from '../../types';
import { FollowButton } from './FollowButton';

interface FriendRowProps {
    friend: User;
    index: number;
    onFriendClick?: (friendId: string) => void;
    onRemoveClick: () => void;
    user: User | null;
}

const FriendRow: React.FC<FriendRowProps> = ({ friend, index, onFriendClick, onRemoveClick, user }) => {


    return (
        <div 
            data-testid={`friend-row-${friend.uid}`}
            onClick={() => onFriendClick?.(friend.uid)}
            className="flex items-center justify-between p-3 pr-4 bg-white border border-brand-cream-dark rounded-2xl shadow-sm animate-slide-in cursor-pointer hover:bg-brand-cream transition-colors"
            style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
        >
            <div className="flex items-center gap-3">
                <div className="relative">
                    <img src={friend.photoURL} className="w-12 h-12 rounded-full bg-brand-cream-dark object-cover" />
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-brand-red text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow ring-2 ring-white">BFF</span>
                </div>
                <div>
                    <p className="font-bold text-brand-dark">{friend.displayName}</p>
                    <p className="text-xs text-brand-dark-light">@{friend.username}</p>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <FollowButton friend={friend} user={user} className="p-2 rounded-lg border border-brand-cream-dark" />
                
                <button 
                    onClick={(e) => { e.stopPropagation(); onRemoveClick(); }}
                    className="px-3 py-1.5 text-brand-red border border-brand-red/60 bg-white rounded-lg text-xs font-bold hover:bg-red-50 flex items-center gap-1"
                >
                    <UserMinus size={14} /> Remove
                </button>
            </div>
        </div>
    );
};

interface FriendsListProps {
  friends: User[];
  onFriendClick?: (friendId: string) => void;
}

export const FriendsList: React.FC<FriendsListProps> = ({ friends, onFriendClick }) => {
  const { refreshUser, currentUser } = useAuth();
  const [confirmRemoveFriend, setConfirmRemoveFriend] = useState<User | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');

  return (
    <div className="space-y-3">
        <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-brand-dark-light uppercase tracking-wider">Friends</h4>
            <span className="px-3 py-1 bg-white border border-brand-cream-dark rounded-full text-xs font-bold text-brand-dark">{friends.length}</span>
        </div>

        {friends.length > 0 ? (
            <div className="space-y-2">
                {friends.map((friend, index) => (
                    <FriendRow 
                        key={friend.uid} 
                        friend={friend} 
                        index={index} 
                        onFriendClick={onFriendClick}
                        onRemoveClick={() => { setConfirmRemoveFriend(friend); setRemoveError(''); }}
                        user={currentUser}
                    />
                ))}
            </div>
        ) : (
            <div className="bg-white border border-brand-cream-dark rounded-2xl p-4 shadow-sm">
                <p className="font-bold text-brand-dark">No friends yet</p>
                <p className="text-sm text-brand-dark-light">Use the search above to add friends by their exact username.</p>
            </div>
        )}

        {/* Remove Friend Confirm Modal */}
        {confirmRemoveFriend && (
          <div className="fixed inset-0 bg-brand-dark/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in" onClick={() => setConfirmRemoveFriend(null)}>
            <div 
              className="bg-white rounded-2xl w-full max-w-sm shadow-2xl border border-brand-cream-dark animate-slide-in"
              onClick={(e) => e.stopPropagation()}
              style={{ animationDelay: '0s', animationFillMode: 'forwards' }}
            >
              <div className="p-4 border-b border-brand-cream-dark flex items-center justify-between">
                <h4 className="font-bold text-brand-dark">Remove Friend</h4>
                <button onClick={() => setConfirmRemoveFriend(null)} className="p-2 rounded-full hover:bg-brand-cream-dark">
                  <X size={18} />
                </button>
              </div>
              <div className="p-4 space-y-3">
                <p className="text-brand-dark text-sm">
                  Are you sure you want to remove <span className="font-bold">@{confirmRemoveFriend.username}</span>? You will stop sharing recommendations with each other.
                </p>
                {removeError && <p className="text-sm text-brand-red font-semibold">{removeError}</p>}
                <div className="flex gap-2">
                  <button 
                    className="flex-1 py-2 bg-brand-cream-dark text-brand-dark font-bold rounded-lg"
                    onClick={() => setConfirmRemoveFriend(null)}
                    disabled={isRemoving}
                  >
                    Cancel
                  </button>
                  <button
                    className="flex-1 py-2 bg-brand-red text-white font-bold rounded-lg hover:bg-brand-red-hover disabled:opacity-60"
                    onClick={async () => {
                      if (!confirmRemoveFriend) return;
                      setIsRemoving(true);
                      setRemoveError('');
                      try {
                        await db.removeFriend(confirmRemoveFriend.uid);
                        if (refreshUser) await refreshUser();
                        setConfirmRemoveFriend(null);
                      } catch (e: any) {
                        captureSilentError("FriendsList.removeFriend", e);
                        setRemoveError(e.message || 'Failed to remove friend.');
                      } finally {
                        setIsRemoving(false);
                      }
                    }}
                    disabled={isRemoving}
                  >
                    {isRemoving ? 'Removing…' : 'Remove Friend'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};
